/**
 * Weapon equip / unequip — the reference-dependent size-fit rule and slot occupancy.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type ApplyCtx, type Result } from "@lancer-vtt/shared";
import { MissingContentError } from "@lancer-vtt/compendium";
import type { RosterCommand, RosterLeafCommand } from "../commands";
import { isFrameIntegratedItem } from "../frameIntegratedGear";
import { licenseRequirement, meetsLicenseRequirement } from "../licenses";
import { checkLoadoutInvariants } from "../loadoutInvariants";
import { weaponFitsSlot } from "../mounts";
import { meetsTalentRequirement, talentRequirement } from "../talentGear";
import { isIntegratedTalentItem } from "../talentIntegratedGear";
import type { RosterState, SlotData } from "../types";
import { requireComposedSlot, slotFittingSizeOf, writeComposedSlot } from "./composedSlot";
import { changed, requireCompendium, requireLoadout, requireMech, requirePilot, withLoadout, withMech, withPilot, type RosterApplyResult } from "./context";

// ── equip_weapon (reference-dependent: size-fit) + occupancy ────────────────────

export function applyEquipWeapon(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "equip_weapon" }>,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;
  const compendiumR = requireCompendium(ctx);
  if (!compendiumR.ok) return compendiumR;
  const compendium = compendiumR.value;

  // Address the COMPOSED array, not `loadout.mounts` directly — a composed index
  // the pilot's current `composedMounts()` doesn't produce
  // (no mount there at all, e.g. the Flex bonus mount when the pilot doesn't hold
  // Improved Armament, or the native-mount count is already at the `<3` cap)
  // naturally rejects here. This IS the cap enforcement; don't re-derive it.
  const resolvedR = requireComposedSlot({
    loadout,
    pilot,
    compendium,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    command: "equip_weapon",
  });
  if (!resolvedR.ok) return resolvedR;
  const { composed, provenance, mount, slot } = resolvedR.value;

  // Slot occupancy (raw-only, one-per-slot): reject equipping over a full slot.
  if (slot.weaponId !== null) {
    return err(
      new CommandError(
        `equip_weapon: slot ${cmd.mountIdx}/${cmd.slotIdx} already holds ${slot.weaponId}; unequip first`,
      ),
    );
  }

  // Size fit (reference-dependent): resolve the weapon, read its declared mount
  // size, and gate against the slot's fitting size. A missing weapon id is a
  // structured `MissingContentError` (a `throw`, not the `Result` channel).
  const weapon = compendium.getById("weapon", cmd.weaponId);
  if (!weapon) {
    throw new MissingContentError("weapon", cmd.weaponId, `mech ${cmd.mechId}`);
  }

  // License gate (single-source `licenses.ts` helper): hard-reject a weapon whose
  // required license rank the pilot hasn't earned. GMS weapons pass via the seeded
  // rank; unlicensed/rank-0 weapons are always allowed.
  if (!meetsLicenseRequirement(pilot, weapon, "weapon")) {
    const req = licenseRequirement(weapon, "weapon");
    return err(
      new CommandError(
        `equip_weapon: weapon ${cmd.weaponId} requires license ${req?.id} rank ${req?.level}; pilot has not earned it`,
      ),
    );
  }

  // Talent gate (single-source `talentGear.ts` helper): hard-reject a weapon
  // whose granting talent rank the pilot hasn't earned (e.g. `mw_prototype_1`
  // needs Engineer r1). GMS license_level 0 lets these pass the license gate
  // above, so this is the gap-closing gate. Items with no `talent_id` pass.
  if (!meetsTalentRequirement(pilot, weapon)) {
    const req = talentRequirement(weapon);
    return err(
      new CommandError(
        `equip_weapon: weapon ${cmd.weaponId} requires talent ${req?.id} rank ${req?.rank}; pilot has not earned it`,
      ),
    );
  }

  // Talent-**integrated** gate (`talentIntegratedGear.ts`'s manual-equip block, single-source
  // with the kit affordance): hard-reject manually equipping an item a talent auto-grants
  // (e.g. `mw_prototype_1` auto-arms at Engineer r1) — it's slot/SP-exempt and already armed
  // at spawn (`spawn.ts`'s merged `integratedWeaponIds`), so a manual equip would double-grant
  // it and (RAW-wrongly) consume a real slot. `special_equipment` (Technophile NHPs) is NOT
  // integrated and stays manually equippable — this predicate keys strictly on the talent's
  // `integrated[]` array, never catching an NHP.
  if (isIntegratedTalentItem(weapon, compendium)) {
    return err(
      new CommandError(
        `equip_weapon: weapon ${cmd.weaponId} is talent-integrated (auto-granted at spawn); it cannot be manually equipped`,
      ),
    );
  }

  // Frame-**integrated** gate (`frameIntegratedGear.ts`, single-source with the kit's
  // `frameIntegratedLock`): the same wall for equipment a *frame* hard-mounts (the Sherman's
  // ZF4 SOLIDCORE). It arms at spawn from the frame's `integrated[]` list, outside `base.mounts`
  // and SP-exempt, so a manual equip would double-grant it and burn a real slot — and it carries
  // no `source`/`license`/`talent_id`, so no other gate here catches it.
  if (isFrameIntegratedItem(cmd.weaponId, compendium)) {
    return err(
      new CommandError(
        `equip_weapon: weapon ${cmd.weaponId} is frame-integrated (hard-mounted by its chassis); it cannot be manually equipped`,
      ),
    );
  }

  const slotFittingSize = slotFittingSizeOf(mount.type, cmd.slotIdx);
  const weaponSize = (weapon as { mount?: string }).mount;
  if (!weaponFitsSlot(slotFittingSize, weaponSize)) {
    return err(
      new CommandError(
        `equip_weapon: weapon ${cmd.weaponId} (size ${weaponSize ?? "unknown"}) does not fit a ${slotFittingSize} slot`,
      ),
    );
  }

  // A freshly-equipped weapon carries no mod yet (modId null; attach_mod adds one).
  const nextSlot: SlotData = { weaponId: cmd.weaponId, modId: null };
  const nextLoadout = writeComposedSlot({
    loadout,
    composed,
    provenance,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    slot: nextSlot,
  });
  const nextMech = withLoadout(mech, nextLoadout);

  // Post-apply loadout invariants (Unique bites here — a 2nd copy of a `tg_unique`
  // weapon; Superheavy bracing too — a Superheavy with no spare mount to brace, or a
  // weapon filling the last spare while a Superheavy is mounted; SP folds in too).
  // Build `nextMech` first, then check the result.
  const violation = checkLoadoutInvariants(nextMech, pilot, compendium);
  if (violation) {
    return err(new CommandError(`equip_weapon: ${violation.message}`));
  }

  const nextPilot = withMech(pilot, nextMech);

  return ok({
    state: withPilot(state, nextPilot),
    inverse: {
      type: "unequip_weapon",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      loadoutId: cmd.loadoutId,
      mountIdx: cmd.mountIdx,
      slotIdx: cmd.slotIdx,
    },
    events: changed(cmd.pilotId),
  });
}

export function applyUnequipWeapon(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "unequip_weapon" }>,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;
  // `unequip_weapon` now needs the compendium too — resolving the FLEX bonus
  // mount tail requires reading the pilot's held core
  // bonuses against it, same as `equip_weapon`.
  const compendiumR = requireCompendium(ctx);
  if (!compendiumR.ok) return compendiumR;
  const compendium = compendiumR.value;

  const resolvedR = requireComposedSlot({
    loadout,
    pilot,
    compendium,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    command: "unequip_weapon",
  });
  if (!resolvedR.ok) return resolvedR;
  const { composed, provenance, slot } = resolvedR.value;

  const prevWeaponId = slot.weaponId;
  // A mod dies with its weapon: clearing the weapon clears its mod too (modId
  // absent/null on the next slot). Capture the prior mod so the inverse can restore
  // both losslessly (the round-trip law) when the unequipped weapon carried one.
  const prevModId = slot.modId ?? null;

  const nextLoadout = writeComposedSlot({
    loadout,
    composed,
    provenance,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    slot: {
      weaponId: null,
      modId: null,
    },
  });
  const nextMech = withLoadout(mech, nextLoadout);
  const nextPilot = withMech(pilot, nextMech);

  // Inverse: re-equip the weapon we just cleared (or, if the slot was already
  // empty, unequip again — a no-op round-trip). When the weapon carried a mod, the
  // inverse re-equips the weapon AND re-attaches the mod, as one atomic batch (the
  // `equip_weapon` gate needs an empty slot, so the weapon must land before its
  // mod) — symmetric, no lossy capture.
  const reequip: RosterCommand = {
    type: "equip_weapon",
    actorId: cmd.actorId,
    pilotId: cmd.pilotId,
    mechId: cmd.mechId,
    loadoutId: cmd.loadoutId,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    weaponId: prevWeaponId ?? "",
  };
  const reattach: RosterCommand = {
    type: "attach_mod",
    actorId: cmd.actorId,
    pilotId: cmd.pilotId,
    mechId: cmd.mechId,
    loadoutId: cmd.loadoutId,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    modId: prevModId ?? "",
  };
  const inverse: RosterCommand =
    prevWeaponId === null
      ? {
          type: "unequip_weapon",
          actorId: cmd.actorId,
          pilotId: cmd.pilotId,
          mechId: cmd.mechId,
          loadoutId: cmd.loadoutId,
          mountIdx: cmd.mountIdx,
          slotIdx: cmd.slotIdx,
        }
      : prevModId === null
        ? reequip
        : { type: "batch", commands: [reequip, reattach] };

  return ok({
    state: withPilot(state, nextPilot),
    inverse,
    events: changed(cmd.pilotId),
  });
}
